import _ from 'lodash';
import tinycolor from 'tinycolor2';

class Canvas {
    x: number;
    y: number;
    width: number;
    height: number;
    normalize: number;
    radius: any;
    fillStyle: string;
    lineWidth: number;
    strokeStyle: string;

    constructor (canvas, normalize = true) {
        this.canvas = canvas;
        this.normalize = normalize;
    }

    get ctx () {
        return this.canvas.getContext('2d');
    }
}

export class CanvasDonut extends Canvas {
    from: number;
    to: number;

    set (x, y, radius, from, to, lineWidth, strokeStyle) {
        this.x = x;
        this.y = y;
        this.radius = radius;
        this.from = from;
        this.to = to;
        this.lineWidth = lineWidth;
        this.strokeStyle = strokeStyle;
    }

    draw (data) {
        this.ctx.beginPath();
        this.ctx.lineWidth = this.lineWidth;
        this.ctx.strokeStyle = this.strokeStyle;
        this.ctx.arc(this.x, this.y, this.radius, this.from, this.to, false);
        this.ctx.stroke();
        let df = this.from;
        const { colors, values } = data;
        const factor = this.normalize ? Math.round((1 / _.sum(values)) * 100) / 100 : 1;
        _.forEach(values, (value, i) => {
            const v = Math.round(value * factor * 100) / 100;
            if (v <= 0) { return; }
            this.ctx.beginPath();
            this.ctx.strokeStyle = colors[i];
            this.ctx.arc(this.x, this.y, this.radius, df, df + ((this.to - this.from) * v), false);
            this.ctx.stroke();
            df += (this.to - this.from) * v;
        });
    }
}

export class CanvasRectangle extends Canvas {
    _setRadius (radius) {
        if (typeof radius === 'number') {
            return { tl: radius, tr: radius, br: radius, bl: radius };
        } else {
            const r = { tl: 0, tr: 0, br: 0, bl: 0 };
            for (let side in r) {
                r[side] = radius[side] || 0;
            }
            return r;
        }
    }

    /**
     * Draws a rounded rectangle using the current state of the canvas.
     * If you omit the last three params, it will draw a rectangle
     * outline with a 5 pixel border radius
     * @param {Number} x The top left x coordinate
     * @param {Number} y The top left y coordinate
     * @param {Number} width The width of the rectangle
     * @param {Number} height The height of the rectangle
     * @param {Number} [radius = 5] The corner radius; It can also be an object to specify different radii for corners
     * @param {Number} [radius.tl = 0] Top left
     * @param {Number} [radius.tr = 0] Top right
     * @param {Number} [radius.br = 0] Bottom right
     * @param {Number} [radius.bl = 0] Bottom left
     * @param {String} fillStyle The color to fill the shape
     * @param {Number} lineWidth The size of the line around the shape
     * @param {String} strokeStyle The color of the stroke line
     */
    set (x, y, width, height, radius = 5, fillStyle, lineWidth, strokeStyle) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.fillStyle = fillStyle;
        this.lineWidth = lineWidth;
        this.strokeStyle = strokeStyle;
        this.radius = this._setRadius(radius);
    }

    draw () {
        this.ctx.beginPath();
        this.ctx.moveTo(this.x + this.radius.tl, this.y);
        this.ctx.lineTo(this.x + this.width - this.radius.tr, this.y);
        this.ctx.quadraticCurveTo(this.x + this.width, this.y, this.x + this.width, this.y + this.radius.tr);
        this.ctx.lineTo(this.x + this.width, this.y + this.height - this.radius.br);
        this.ctx.quadraticCurveTo(this.x + this.width, this.y + this.height, this.x + this.width - this.radius.br, this.y + this.height);
        this.ctx.lineTo(this.x + this.radius.bl, this.y + this.height);
        this.ctx.quadraticCurveTo(this.x, this.y + this.height, this.x, this.y + this.height - this.radius.bl);
        this.ctx.lineTo(this.x, this.y + this.radius.tl);
        this.ctx.quadraticCurveTo(this.x, this.y, this.x + this.radius.tl, this.y);
        if (this.fillStyle) {
            this.ctx.fillStyle = this.fillStyle;
            this.ctx.fill();
        }
        this.ctx.closePath();
    }
}

export class CanvasCircle extends Canvas {
    set (x, y, radius = 5, fillStyle = '#ffffff', lineWidth, strokeStyle, withShadow = false, fontSize = 12) {
        this.x = x;
        this.y = y;
        this.radius = radius;
        this.fillStyle = fillStyle;
        this.lineWidth = lineWidth;
        this.strokeStyle = strokeStyle;
        this.fontSize = fontSize < 12 ? 12 : fontSize;
        this.withShadow = withShadow;
    }

    draw () {
        this.ctx.beginPath();
        this.ctx.arc(this.x, this.y, this.radius, 0, 2 * Math.PI, false);
        if(this.withShadow) {
            this.ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            this.ctx.shadowBlur = 4;
            this.ctx.shadowOffsetX = 0;
            this.ctx.shadowOffsetY = -2;
        }
        if (this.fillStyle) {
            this.ctx.fillStyle = this.fillStyle;
            this.ctx.fill();
        }
        if (this.lineWidth && this.strokeStyle) {
            this.ctx.lineWidth = this.lineWidth;
            this.ctx.strokeStyle = this.strokeStyle;
            this.ctx.stroke();
        }
        this.ctx.closePath();
    }

    text (text) {
        const textY = this.y + (this.fontSize > 12 ? this.fontSize - 12 + 2 : 4);

        this.ctx.textAlign = 'center';
        this.ctx.fillStyle = tinycolor(this.fillStyle).getBrightness() < 160 ? '#FFF' : '#303030';
        this.ctx.font = `bold ${this.fontSize}px 'Roboto Condensed', Arial, Sans-serif`;
        this.ctx.fillText(text, this.x + 1, textY);
    }
}

export class CanvasTriangle extends Canvas {
    rotate: number;
    points: any;

    _rotatePoints (points) {
        return _.reduce(points, (acc, val, key) => {
            acc[key] = {};

            acc[key].x = val.x - this.x;
            acc[key].y = val.y - this.y;

            acc[key].x = acc[key].x * Math.cos(this.rotate * Math.PI / 180.0) - acc[key].y * Math.sin(this.rotate * Math.PI / 180.0);
            acc[key].y = acc[key].x * Math.sin(this.rotate * Math.PI / 180.0) + acc[key].y * Math.cos(this.rotate * Math.PI / 180.0);

            acc[key].x = acc[key].x + this.x;
            acc[key].y = acc[key].y + this.y;
            return acc;
        }, {});
    }

    set (x, y, width, height, rotate = 0, fillStyle = '#ffffff', lineWidth, strokeStyle) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.rotate = rotate;
        this.fillStyle = fillStyle;
        this.lineWidth = lineWidth;
        this.strokeStyle = strokeStyle;
        this.points = this._rotatePoints({
            a: {
                x: x,
                y: y + (height / 2)
            },
            b: {
                x: x + (width / 2),
                y: y - (height / 2)
            },
            c: {
                x: x - (width / 2),
                y: y - (height / 2)
            }
        });
    }

    draw () {
        this.ctx.beginPath();
        // triangle 1, at left
        this.ctx.moveTo(this.points.a.x, this.points.a.y);
        this.ctx.lineTo(this.points.b.x, this.points.b.y);
        this.ctx.lineTo(this.points.c.x, this.points.c.y);
        this.ctx.fill(); // connect and fill
        if (this.fillStyle) {
            this.ctx.fillStyle = this.fillStyle;
            this.ctx.fill();
        }
        if (this.lineWidth && this.strokeStyle) {
            this.ctx.lineWidth = this.lineWidth;
            this.ctx.strokeStyle = this.strokeStyle;
            this.ctx.stroke();
        }
        this.ctx.closePath();
    }

    text (text) {
        this.ctx.textAlign = 'center';
        this.ctx.fillStyle = tinycolor(this.fillStyle).getBrightness() < 160 ? '#FFF' : '#303030';
        this.ctx.font = `bold 12px 'Roboto Condensed', Arial, Sans-serif`;
        this.ctx.fillText(text, this.x + 2, this.y + 4);
    }
}



// WEBPACK FOOTER //
// ./src/helpers/canvas.helper.js